import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import FancyButton from '../components/ui/fancyButton';
import FormField from '../components/ui/FormField';
import TextInput from '../components/ui/TextInput';
import TextArea from '../components/ui/TextArea';
import Select from '../components/ui/Select';
import {
  generateCourseContentThunk,
  selectCourseLoading,
  selectCourseError,
} from '../redux/courseDetailsSlice';
import { selectProfile as selectStudentProfile } from '../redux/profileSlice';
import './CourseCreationForm.css';

const CourseCreationForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const loading = useSelector(selectCourseLoading);
  const error = useSelector(selectCourseError);
  const profile = useSelector(selectStudentProfile);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    objectives: '',
    priorKnowledge: '',
    difficultyLevel: 'Beginner',
    weeklyHours: '3',
    lessons: '6',
    referenceBooks: '',
  });

  useEffect(() => {
    try {
      const saved = localStorage.getItem('courseDraft');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object') {
          setFormData((prev) => ({ ...prev, ...parsed }));
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Failed to load course draft:', err);
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      localStorage.setItem('courseDraft', JSON.stringify(formData));
    }, 500); // save after 500ms of inactivity
    return () => clearTimeout(timeout);
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      // eslint-disable-next-line no-console
      console.warn('Course title cannot be empty.');
      return;
    }

    const toStringArray = (value) => {
      if (Array.isArray(value))
        return value
          .map(String)
          .map((str) => str.trim())
          .filter(Boolean);
      if (typeof value === 'string')
        return value
          .split(',')
          .map((str) => str.trim())
          .filter(Boolean);
      return [];
    };

    const courseData = {
      ...formData,
      objectives: toStringArray(formData.objectives),
      priorKnowledge: toStringArray(formData.priorKnowledge),
      referenceBooks: toStringArray(formData.referenceBooks),
      weeklyHours: Number.isNaN(parseInt(formData.weeklyHours))
        ? 0
        : parseInt(formData.weeklyHours),
      lessons: Number.isNaN(parseInt(formData.lessons)) ? 0 : parseInt(formData.lessons),
    };

    try {
      const action = await dispatch(
        generateCourseContentThunk({
          courseData,
          profileData: profile,
        })
      );

      if (generateCourseContentThunk.fulfilled.match(action)) {
        localStorage.removeItem('courseDraft');
        navigate('/dashboard', {
          state: { openCourseId: action.payload?.id || null },
        });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('❌ Error creating course: ' + err);
    }
  };

  return (
    <div className="ml-course-creation-page">
      <div className="ml-course-creation-card">
        <div className="ml-course-creation-card__header">
          <h1 className="ml-course-creation-card__title">Design Your Own Course</h1>
        </div>

        <div className="ml-course-creation-card__body">
          {error && <div className="ml-form-error">{error}</div>}

          <form onSubmit={handleSubmit} className="ml-course-creation-form">
            <h2 className="ml-form-section-title">📝 Course Basics</h2>

            <FormField label="Course Title" htmlFor="title" required>
              <TextInput
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g., Build Your First AI-Powered App"
                required
              />
            </FormField>

            <FormField
              label={
                <span>
                  What do <em>you</em> want to learn?
                </span>
              }
              htmlFor="description"
              required
            >
              <TextArea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe your vision. What excites you to explore?"
                rows={3}
                required
              />
            </FormField>

            <h2 className="ml-form-section-title">🎯 Your Learning Vision</h2>

            <FormField
              label="What goals matter most to you?"
              htmlFor="objectives"
              helpText="Comma-separated list"
            >
              <TextArea
                id="objectives"
                name="objectives"
                value={formData.objectives}
                onChange={handleChange}
                placeholder="e.g., Build mobile apps, Understand APIs, Publish a portfolio"
                rows={2}
              />
            </FormField>

            <FormField
              label="What do you already know?"
              htmlFor="priorKnowledge"
              helpText="Optional, comma-separated"
            >
              <TextArea
                id="priorKnowledge"
                name="priorKnowledge"
                value={formData.priorKnowledge}
                onChange={handleChange}
                placeholder="e.g., JavaScript, some React, maybe a bit of Node.js"
                rows={2}
              />
            </FormField>

            <h2 className="ml-form-section-title">📅 Course Structure</h2>

            <FormField label="Difficulty Level" htmlFor="difficultyLevel">
              <Select
                id="difficultyLevel"
                name="difficultyLevel"
                value={formData.difficultyLevel}
                onChange={handleChange}
                options={[
                  { value: 'Beginner', label: 'Beginner' },
                  { value: 'Intermediate', label: 'Intermediate' },
                  { value: 'Advanced', label: 'Advanced' },
                ]}
              />
            </FormField>

            <div className="ml-form-row">
              <FormField label="Number of Lessons" htmlFor="lessons">
                <TextInput
                  id="lessons"
                  type="number"
                  name="lessons"
                  value={formData.lessons}
                  onChange={handleChange}
                  min="1"
                  max="30"
                />
              </FormField>

              <FormField label="Weekly Hours" htmlFor="weeklyHours">
                <TextInput
                  id="weeklyHours"
                  type="number"
                  name="weeklyHours"
                  value={formData.weeklyHours}
                  onChange={handleChange}
                  min="1"
                  max="40"
                />
              </FormField>
            </div>

            <h2 className="ml-form-section-title">📚 Resources</h2>

            <FormField
              label="Reference Books"
              htmlFor="referenceBooks"
              helpText="Optional, comma-separated"
            >
              <TextArea
                id="referenceBooks"
                name="referenceBooks"
                value={formData.referenceBooks}
                onChange={handleChange}
                placeholder="List key books or sources (optional)"
                rows={2}
              />
            </FormField>

            <div className="ml-form-actions">
              <FancyButton
                variant="tertiary"
                onClick={() => {
                  if (window.confirm('Discard draft and return to dashboard?')) {
                    localStorage.removeItem('courseDraft');
                    navigate('/dashboard');
                  }
                }}
                type="button"
              >
                Cancel
              </FancyButton>

              <FancyButton variant="primary" type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin" /> Creating...
                  </>
                ) : (
                  'Create Course'
                )}
              </FancyButton>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CourseCreationForm;
